import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function EmailDialog({ doc, exp }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const to = doc.party?.email?.trim();

  const send = async () => {
    setSending(true);
    try {
      const pdf = await exp.makePdf();
      const fd = new FormData();
      fd.append("pdf", new Blob([pdf.output("arraybuffer")], { type: "application/pdf" }), `${doc.number}.pdf`);
      fd.append("message", message);
      const { data } = await api.post(`/documents/${doc.id}/send`, fd);
      qc.invalidateQueries({ queryKey: ["document", doc.id] });
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success(`Sent to ${data.to}`);
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="email-button" className="btn-press h-10 px-4 text-sm font-semibold border border-border hover:border-foreground flex items-center gap-2">
        <Mail className="w-4 h-4" strokeWidth={1.5} /> Email
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Email {doc.number}</DialogTitle>
            <DialogDescription>The PDF is attached exactly as previewed, with a link to view it online.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <span className="overline block mb-2">Recipient</span>
              {to ? (
                <div className="field flex items-center font-mono text-sm bg-muted" data-testid="email-recipient">{to}</div>
              ) : (
                <p className="text-sm text-destructive" data-testid="email-no-recipient">
                  No email on this document. <Link to={`/documents/${doc.id}/edit`} className="underline">Add the client's email</Link> first.
                </p>
              )}
            </div>
            <label className="block">
              <span className="overline block mb-2">Personal note (optional)</span>
              <textarea className="field-area" data-testid="email-message" value={message} maxLength={1000} onChange={(e) => setMessage(e.target.value)} placeholder="Thanks for your business — payment details are on the invoice." />
            </label>
            {doc.last_sent_at && <p className="text-xs text-muted-foreground" data-testid="email-last-sent">Last sent to {doc.last_sent_to} on {new Date(doc.last_sent_at).toLocaleString()}</p>}
            <button onClick={send} disabled={!to || sending} data-testid="email-send-button" className="btn-press w-full h-11 bg-primary text-primary-foreground font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#C04E2D] disabled:opacity-50">
              <Send className="w-4 h-4" strokeWidth={1.5} /> {sending ? "Generating PDF & sending…" : "Send email"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
